import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Send, CheckCircle2, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { CONTACT_SUBJECTS } from "@/data/products";
import { MaskedLines, Eyebrow, EASE } from "@/components/site/Motion";

const inputCls = "w-full rounded-xl border border-[#D8D7CE] bg-white px-4 py-3.5 text-[15px] text-[#070B2E] placeholder:text-[#6B6F88] outline-none transition-[border-color,box-shadow] focus:border-[#0B14A0] focus:shadow-[0_0_0_4px_rgba(11,20,160,0.12)]";

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="block text-sm font-semibold text-[#1C2150] mb-2">{label}{required && <span className="text-[#0B14A0]"> *</span>}</span>
    {children}
  </label>
);

const Success = ({ onReset }) => (
  <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, ease: EASE }} className="text-center py-16" data-testid="contact-form-success">
    <CheckCircle2 className="w-14 h-14 mx-auto text-[#0B14A0]" />
    <h2 className="font-display text-3xl font-bold mt-6">Tack för ditt meddelande!</h2>
    <p className="mt-3 text-[#3A3F5C]">Vi återkommer så snart vi kan.</p>
    <button onClick={onReset} className="mt-8 rounded-full border border-[#D8D7CE] px-6 py-3 font-semibold hover:border-[#070B2E]" data-testid="contact-form-reset">Skicka ett nytt meddelande</button>
  </motion.div>
);

const initial = (subject) => ({ name: "", company: "", email: "", phone: "", subject, message: "" });

export default function Contact() {
  const [params] = useSearchParams();
  const pre = params.get("subject");
  const startSubject = CONTACT_SUBJECTS.includes(pre) ? pre : pre ? "Övrigt" : "Boka demo";
  const [form, setForm] = useState(() => initial(startSubject));
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      setForm(initial(startSubject));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="-mt-20 pt-32 lg:pt-40 pb-28 grid-lines" data-testid="contact-page">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <Eyebrow>Kontakt</Eyebrow>
          <MaskedLines testId="contact-title" className="font-display font-bold tracking-[-0.035em] leading-[0.95] text-5xl sm:text-6xl lg:text-7xl mt-6" lines={["Låt oss", "prata."]} />
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE, delay: 0.5 }} className="mt-8 max-w-md text-base sm:text-lg text-[#3A3F5C] leading-relaxed">
            Boka en kostnadsfri demo eller få mer information om en specifik produkt eller tjänst – fyll i formuläret så återkommer vi inom kort.
          </motion.p>
        </div>
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: EASE, delay: 0.3 }} className="lg:col-span-7">
          <div className="rounded-[28px] bg-white border border-[#E3E2DA] p-6 sm:p-10 shadow-[0_40px_80px_-50px_rgba(7,11,46,0.5)]">
            {sent ? <Success onReset={() => setSent(false)} /> : (
              <form onSubmit={submit} className="grid sm:grid-cols-2 gap-5" data-testid="contact-form">
                <Field label="Namn" required><input required minLength={2} value={form.name} onChange={set("name")} className={inputCls} data-testid="contact-form-name-input" /></Field>
                <Field label="Företag / byrå"><input value={form.company} onChange={set("company")} className={inputCls} data-testid="contact-form-company-input" /></Field>
                <Field label="E-post" required><input required type="email" value={form.email} onChange={set("email")} className={inputCls} data-testid="contact-form-email-input" /></Field>
                <Field label="Telefon"><input type="tel" value={form.phone} onChange={set("phone")} className={inputCls} data-testid="contact-form-phone-input" /></Field>
                <div className="sm:col-span-2">
                  <Field label="Vad gäller det?" required>
                    <select required value={form.subject} onChange={set("subject")} className={`${inputCls} appearance-none cursor-pointer`} data-testid="contact-form-subject-select">
                      {CONTACT_SUBJECTS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </Field>
                </div>
                <div className="sm:col-span-2">
                  <Field label="Meddelande" required><textarea required minLength={5} rows={6} value={form.message} onChange={set("message")} className={`${inputCls} resize-y`} data-testid="contact-form-message-input" /></Field>
                </div>
                <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-2">
                  <p className="text-xs text-[#4A4F6E] max-w-xs">Dina uppgifter används endast för att besvara din förfrågan.</p>
                  <button type="submit" disabled={loading} data-testid="contact-form-submit-button" className="inline-flex items-center justify-center gap-2 rounded-full bg-[#0B14A0] text-white px-8 py-4 font-semibold transition-[background-color,opacity] hover:bg-[#070B2E] disabled:opacity-60">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Skicka förfrågan
                  </button>
                </div>
              </form>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
